import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { FormFeedback } from '@/components/ui/form-feedback';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/expenses/capture/NativeSelect';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types';
import { usePendingWrite } from '@/lib/use-pending-write';
import { countryOptions } from '@/profile/countries';
import { problemCode } from '../api/problem';
import { taxCodeIsValid, vatNumberIsValid } from './organization-details-checks';

type Details = components['schemas']['OrganizationDetailsResponse'];
type Address = components['schemas']['OrganizationAddressDto'];

const ADDRESS_FIELDS = ['street', 'postalCode', 'city', 'province'] as const;
type AddressField = (typeof ADDRESS_FIELDS)[number];
type Field = AddressField | 'vatNumber' | 'taxCode';
type Values = Record<Field | 'country', string>;

const ITALY = 'IT';
const MAX = { street: 255, postalCode: 16, city: 120, province: 120 } as const;

type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly details: Details };

function toValues(details: Details): Values {
  return {
    street: details.address?.street ?? '',
    postalCode: details.address?.postalCode ?? '',
    city: details.address?.city ?? '',
    province: details.address?.province ?? '',
    country: details.address?.country ?? details.country,
    vatNumber: details.vatNumber ?? '',
    taxCode: details.taxCode ?? '',
  };
}

const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/** No address is given while its four text fields are empty. */
const hasAddress = (values: Values) => ADDRESS_FIELDS.some((field) => values[field].trim() !== '');

function toAddress(values: Values): Address | null {
  if (!hasAddress(values)) return null;
  const province = values.province.trim();
  return {
    street: values.street.trim(),
    postalCode: values.postalCode.trim(),
    city: values.city.trim(),
    province: province === '' ? null : values.country === ITALY ? province.toUpperCase() : province,
    country: values.country,
  };
}

function putDetails(organizationId: string, values: Values) {
  return apiClient.PUT('/v1/administration/organizations/{organizationId}/details', {
    params: { path: { organizationId } },
    body: {
      address: toAddress(values),
      vatNumber: orNull(values.vatNumber),
      taxCode: orNull(values.taxCode),
    },
  });
}

/** The key of a field's problem under `organizationDetails.errors`, or `null` when it is fine. */
function problemOf(field: Field, values: Values, checksCountry: string): string | null {
  const value = values[field].trim();
  const foreign = checksCountry !== ITALY;
  switch (field) {
    case 'vatNumber':
      if (value === '') return null;
      return vatNumberIsValid(value, checksCountry)
        ? null
        : foreign
          ? 'vatNumberForeign'
          : 'vatNumber';
    case 'taxCode':
      if (value === '') return null;
      return taxCodeIsValid(value, checksCountry) ? null : foreign ? 'taxCodeForeign' : 'taxCode';
    default: {
      // The address is all or nothing: once one of its fields is written, the others are asked.
      if (!hasAddress(values)) return null;
      const italy = values.country === ITALY;
      if (value === '') return field === 'province' && !italy ? null : 'required';
      if (value.length > MAX[field]) return 'tooLong';
      if (italy && field === 'postalCode' && !/^\d{5}$/.test(value)) return 'postalCode';
      if (italy && field === 'province' && !/^[A-Za-z]{2}$/.test(value)) return 'province';
      return null;
    }
  }
}

/**
 * «Dati dell'organizzazione» (decision 192, #837): the address, VAT number and tax code Note spese
 * prints in the PDF's company block, on the organization's Riepilogo. Whoever administers the
 * organization reads and writes them (`GET`/`PUT /v1/administration/organizations/{id}/details`).
 * The checks follow the address's country, the organization's own while there is no address.
 * Errors show on the field as the person types (U10), «Salva» stays disabled with its reason while
 * nothing changed (U11), and a save answers with a short confirmation (U9).
 */
export function OrganizationDetailsCard({ organizationId }: { readonly organizationId: string }) {
  const { t, i18n } = useTranslation('administration');
  const titleId = useId();
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [values, setValues] = useState<Values | null>(null);
  const [touched, setTouched] = useState<ReadonlySet<Field>>(new Set());
  const [serverField, setServerField] = useState<{ field: Field; key: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const { pending, run } = usePendingWrite();
  const countries = useMemo(() => countryOptions(i18n.language), [i18n.language]);
  // The organization on screen now: a save that completes for another one is dropped, so it can
  // never fill this organization's form (the R-c re-review).
  const current = useRef(organizationId);
  current.current = organizationId;

  const read = useCallback(() => {
    let cancelled = false;
    setLoad({ status: 'loading' });
    apiClient
      .GET('/v1/administration/organizations/{organizationId}/details', {
        params: { path: { organizationId } },
      })
      .then(
        ({ data, response }) => {
          if (cancelled) return;
          if (data === undefined || !response.ok) {
            setLoad({ status: 'error' });
            return;
          }
          setLoad({ status: 'ready', details: data });
          setValues(toValues(data));
        },
        () => {
          if (!cancelled) setLoad({ status: 'error' });
        },
      );
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(read, [read]);

  if (load.status !== 'ready' || values === null) {
    return (
      <section aria-labelledby={titleId} className="border-border bg-card rounded-xl border p-5">
        <h3 id={titleId} className="card-label">
          {t('organizationDetails.title')}
        </h3>
        {load.status === 'error' ? (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <p className="text-sm">{t('organizationDetails.loadFailed')}</p>
            <Button variant="outline" className="min-h-11" onClick={read}>
              {t('organizationDetails.retry')}
            </Button>
          </div>
        ) : (
          <div className="bg-muted/60 mt-4 h-24 animate-pulse rounded-lg" aria-busy="true" />
        )}
      </section>
    );
  }

  const { details } = load;
  const saved = toValues(details);
  const checksCountry = hasAddress(values) ? values.country : details.country;
  const foreign = checksCountry !== ITALY;
  const inItaly = values.country === ITALY;
  const allFields: readonly Field[] = [...ADDRESS_FIELDS, 'vatNumber', 'taxCode'];
  // Changed when what «Salva» would send differs from what is saved (a country alone is no address).
  const payload = (from: Values) =>
    JSON.stringify([toAddress(from), orNull(from.vatNumber), orNull(from.taxCode)]);
  const changed = payload(values) !== payload(saved);
  const errorKey = (field: Field): string | null =>
    serverField?.field === field
      ? serverField.key
      : touched.has(field)
        ? problemOf(field, values, checksCountry)
        : null;
  const hasErrors = allFields.some((field) => problemOf(field, values, checksCountry) !== null);

  const change = (key: keyof Values, value: string) => {
    setValues((previous) => (previous === null ? previous : { ...previous, [key]: value }));
    setConfirmation(null);
    setFormError(null);
    if (serverField?.field === key) setServerField(null);
  };

  const save = async () => {
    // Enter submits the form even while «Salva» is disabled: nothing changed, nothing to send.
    if (!changed) return;
    setTouched(new Set(allFields));
    if (hasErrors) return;
    setFormError(null);
    setConfirmation(null);
    const target = organizationId;
    let answer: Awaited<ReturnType<typeof putDetails>>;
    try {
      answer = await putDetails(target, values);
    } catch {
      if (current.current !== target) return;
      // A network failure or a failed token refresh: nothing changed, the values stay for a retry.
      setFormError(t('organizationDetails.errors.failed'));
      return;
    }
    if (current.current !== target) return;
    const { data, error, response } = answer;
    if (data === undefined || !response.ok) {
      const code = problemCode(error);
      if (code === 'VAT_NUMBER_INVALID' || code === 'TAX_CODE_INVALID') {
        const field = code === 'VAT_NUMBER_INVALID' ? 'vatNumber' : 'taxCode';
        setServerField({ field, key: foreign ? `${field}Foreign` : field });
        return;
      }
      setFormError(
        t(
          response.status === 400
            ? 'organizationDetails.errors.invalid'
            : 'organizationDetails.errors.failed',
        ),
      );
      return;
    }
    setLoad({ status: 'ready', details: data });
    setValues(toValues(data));
    setTouched(new Set());
    const message = t('organizationDetails.saved');
    setConfirmation(message);
    toast.success(message);
  };

  const fieldId = (field: Field | 'country') => `${titleId}-${field}`;
  const describedBy = (field: Field, hint?: string) =>
    [errorKey(field) !== null ? `${fieldId(field)}-error` : null, hint ?? null]
      .filter((id): id is string => id !== null)
      .join(' ') || undefined;
  const fieldError = (field: Field) => {
    const key = errorKey(field);
    return key === null ? null : (
      <p id={`${fieldId(field)}-error`} className="text-destructive text-sm">
        {t(`organizationDetails.errors.${key}` as 'organizationDetails.errors.invalid')}
      </p>
    );
  };
  const touch = (field: Field) => setTouched((previous) => new Set(previous).add(field));

  const textField = (
    field: Field,
    options: { label: string; hintText?: string; mono?: boolean; extra?: object },
  ) => (
    <div className="space-y-2">
      <label className="field-label" htmlFor={fieldId(field)}>
        {options.label}
      </label>
      <Input
        id={fieldId(field)}
        value={values[field]}
        onChange={(event) => change(field, event.target.value)}
        onBlur={() => touch(field)}
        aria-invalid={errorKey(field) !== null}
        aria-describedby={describedBy(
          field,
          options.hintText === undefined ? undefined : `${fieldId(field)}-hint`,
        )}
        {...options.extra}
        className={options.mono === true ? 'min-h-11 font-mono' : 'min-h-11'}
      />
      {options.hintText !== undefined && (
        <p id={`${fieldId(field)}-hint`} className="text-muted-foreground text-xs">
          {options.hintText}
        </p>
      )}
      {fieldError(field)}
    </div>
  );

  return (
    <section aria-labelledby={titleId} className="border-border bg-card rounded-xl border p-5">
      <h3 id={titleId} className="card-label">
        {t('organizationDetails.title')}
      </h3>
      <p className="text-muted-foreground mt-1 text-sm">{t('organizationDetails.why')}</p>
      <form
        className="mt-4 max-w-2xl space-y-5"
        aria-labelledby={titleId}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void run(save);
        }}
      >
        <fieldset className="@container space-y-4">
          <legend className="field-label mb-2">{t('organizationDetails.address')}</legend>
          {textField('street', {
            label: t('organizationDetails.street'),
            extra: { autoComplete: 'street-address' },
          })}
          {/* By the form's own width, not the viewport's: a side panel is narrow on a wide screen. */}
          <div className="grid gap-4 @xl:grid-cols-[minmax(0,10rem)_minmax(0,1fr)_minmax(0,10rem)]">
            {textField('postalCode', {
              label: t('organizationDetails.postalCode'),
              extra: { autoComplete: 'postal-code', inputMode: inItaly ? 'numeric' : 'text' },
            })}
            {textField('city', {
              label: t('organizationDetails.city'),
              extra: { autoComplete: 'address-level2' },
            })}
            {textField('province', {
              label: t('organizationDetails.province'),
              extra: { autoComplete: 'address-level1', maxLength: inItaly ? 2 : 120 },
            })}
          </div>
          <div className="max-w-sm space-y-2">
            <label className="field-label" htmlFor={fieldId('country')}>
              {t('organizationDetails.country')}
            </label>
            <NativeSelect
              id={fieldId('country')}
              className="min-h-11"
              autoComplete="country"
              value={values.country}
              // The country is part of the address: chosen once the address is being written.
              disabled={!hasAddress(values)}
              aria-describedby={hasAddress(values) ? undefined : fieldId('country') + '-hint'}
              onChange={(event) => change('country', event.target.value)}
            >
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </NativeSelect>
            {!hasAddress(values) && (
              <p id={fieldId('country') + '-hint'} className="text-muted-foreground text-xs">
                {t('organizationDetails.countryHint')}
              </p>
            )}
          </div>
        </fieldset>
        <div className="grid gap-4 sm:grid-cols-2">
          {textField('vatNumber', {
            label: t('organizationDetails.vatNumber'),
            mono: true,
            extra: {
              inputMode: foreign ? 'text' : 'numeric',
              autoComplete: 'off',
              spellCheck: false,
            },
          })}
          {textField('taxCode', {
            label: t('organizationDetails.taxCode'),
            hintText: foreign
              ? t('organizationDetails.foreignHint')
              : t('organizationDetails.taxCodeHint'),
            mono: true,
            extra: {
              autoCapitalize: 'characters',
              autoComplete: 'off',
              spellCheck: false,
            },
          })}
        </div>

        {formError !== null && <FormFeedback tone="error">{formError}</FormFeedback>}
        {confirmation !== null && <FormFeedback tone="success">{confirmation}</FormFeedback>}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            className="min-h-11"
            disabled={!changed || pending}
            aria-describedby={!changed ? `${titleId}-unchanged` : undefined}
          >
            {t('organizationDetails.save')}
          </Button>
          {!changed && (
            <span id={`${titleId}-unchanged`} className="text-muted-foreground text-sm">
              {t('organizationDetails.unchanged')}
            </span>
          )}
        </div>
      </form>
    </section>
  );
}
